import React, { useEffect, useState } from 'react';

import { Icon, IconButton, Spinner } from 'components';
import Modal from 'components/Modal';
import { ButtonV3, DropdownV3, InputV2 } from 'components/ComponentV2';
import Tooltip from 'components/Tooltip';
import { useSponsorshipPlans } from 'pages/sponsorships/useSponsorshipPlans';
import { useCommonList } from 'lib/contexts/common-list-context';
import { Option } from 'lib/models/option';
import {
  CreateNewPlanPayload,
  SponsorShipPlanType,
} from 'lib/models/sponsorship-plans';
import { CustomFeatures } from '../CustomFeatures/CustomFeatures.component';

import styles from './CreatePlanModal.module.scss';

interface CreatePlanModalProps {
  open: boolean;
  editPlanData?: SponsorShipPlanType;
  onClose: () => void;
}

export const CreatePlanModal: React.FC<CreatePlanModalProps> = (
  props: CreatePlanModalProps
) => {
  const { open, editPlanData, onClose } = props;
  const {
    huzzleFeatures,
    durationTypes,
    isLoading,
    createNewPlan,
    updatePlan,
  } = useSponsorshipPlans();
  const { currencyOptions } = useCommonList();

  const [frequencyOptions, setFrequencyOptions] = useState<Option[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [otherFeatures, setOtherFeatures] = useState<string[]>(['']);
  const [planPayload, setPlanPayload] = useState<CreateNewPlanPayload>(
    {} as unknown as CreateNewPlanPayload
  );

  useEffect(() => {
    if (!open) {
      setPlanPayload({} as unknown as CreateNewPlanPayload);
      setOtherFeatures(['']);
      setSelectedFeatures([]);
    } else {
      if (editPlanData) {
        setPlanPayload(editPlanData as unknown as CreateNewPlanPayload);
        setOtherFeatures(
          editPlanData.custom_features.length > 0
            ? editPlanData.custom_features
            : ['']
        );
        onSelectHuzzleFeatures(editPlanData.huzzle_features);
      } else {
        setPlanPayload((payload) => ({
          ...payload,
          duration_type: frequencyOptions[0].value,
        }));
        if (currencyOptions) {
          setPlanPayload((payload) => ({
            ...payload,
            currency: currencyOptions[0].label,
          }));
        }
      }
    }
  }, [open]);

  useEffect(() => {
    if (durationTypes) {
      const options = Object.keys(durationTypes).map((duration: string) => {
        return {
          id: durationTypes[duration].toString(),
          label: duration.replace('_', ' '),
          value: duration,
        };
      });
      setFrequencyOptions(options);
      setPlanPayload((payload) => ({
        ...payload,
        duration_type: options[0].value,
      }));
    }

    if (currencyOptions && !planPayload.currency) {
      setPlanPayload((payload) => ({
        ...payload,
        currency: currencyOptions[0].label,
      }));
    }
  }, [durationTypes, currencyOptions]);

  const onSelectHuzzleFeatures = (values: Array<string>) => {
    setSelectedFeatures(values);
    const huzzle_feature_ids: number[] = values.map(
      (value: string) => huzzleFeatures![value]
    );
    setPlanPayload((payload) => ({
      ...payload,
      huzzle_feature_ids,
    }));
  };

  const onOtherFeaturesChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const tempFeatures = otherFeatures.slice(0);
    tempFeatures[index] = event.target.value;
    setOtherFeatures(tempFeatures);
    setPlanPayload((payload) => ({
      ...payload,
      custom_features: tempFeatures.filter((feature) => feature != ' '),
    }));
  };

  const onSavePlan = async () => {
    const response = editPlanData
      ? await updatePlan(editPlanData.id, planPayload)
      : await createNewPlan(planPayload);
    if (response) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      contentClassName={styles.CreatePlanModal}
    >
      {isLoading && <Spinner wrapper showBackDrop size="small" />}
      <div className={styles.CreatePlanModalHeader}>
        <p>{editPlanData ? 'Edit' : 'Create'} plan</p>
        <IconButton
          className={styles.closeIcon}
          iconName="close"
          size="xsmall"
          onClick={onClose}
        />
      </div>
      <div className={styles.CreatePlanModalContent}>
        <div>
          <p className={styles.label}>Plan name</p>
          <InputV2
            value={planPayload.title || ''}
            onChange={(event) =>
              setPlanPayload((payload) => ({
                ...payload,
                title: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <div className={styles.labelWrapper}>
            <p className={styles.label}>Short description</p>
            <span className={styles.optional}>(optional)</span>
            <Tooltip
              color="dark"
              content="Adding a short description of this plan may attract more companies."
              isV2Design
              position="top"
            >
              <Icon
                iconName="icon_info"
                size="small"
                className={styles.infoIcon}
              />
            </Tooltip>
          </div>
          <InputV2
            value={planPayload.description || ''}
            placeholder="For companies looking for most talented students."
            onChange={(event) =>
              setPlanPayload((payload) => ({
                ...payload,
                description: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <div className={styles.labelWrapper}>
            <p className={styles.label}>Price</p>
            <span className={styles.optional}>(optional)</span>
          </div>
          <div className={styles.inputWrapper}>
            <InputV2
              type="number"
              placeholder="Add a price or leave blank"
              value={planPayload.cost || ''}
              onChange={(event) =>
                setPlanPayload((payload) => ({
                  ...payload,
                  cost: event.target.value ? Number(event.target.value) : null,
                }))
              }
            />
            <DropdownV3
              label=""
              options={currencyOptions || []}
              value={planPayload.currency || ''}
              onChange={(value: string) =>
                setPlanPayload((payload) => ({ ...payload, currency: value }))
              }
              selectedValueLabel={planPayload.currency}
              isValid={true}
              className={styles.singleSelect}
              dropdownClassName={styles.singleSelectList}
            />
            <DropdownV3
              label=""
              options={frequencyOptions}
              value={planPayload.duration_type || ''}
              onChange={(value: string) =>
                setPlanPayload((payload) => ({
                  ...payload,
                  duration_type: value,
                }))
              }
              selectedValueLabel={planPayload.duration_type?.replace('_', ' ')}
              isValid={true}
              className={styles.singleSelect}
              dropdownClassName={styles.singleSelectList}
            />
          </div>
        </div>
        <CustomFeatures
          selectedHuzzleFeatures={selectedFeatures}
          onSelectHuzzleFeatures={onSelectHuzzleFeatures}
          otherSelectedFeatures={otherFeatures}
          onOtherFeaturesChange={onOtherFeaturesChange}
          onAddRow={() =>
            setOtherFeatures((otherFeatures) => [...otherFeatures, ''])
          }
        />
      </div>
      <div className={styles.CreatePlanModalAction}>
        <ButtonV3
          disabled={
            !planPayload.title ||
            !planPayload.huzzle_feature_ids ||
            (planPayload.huzzle_feature_ids &&
              !planPayload.huzzle_feature_ids.length)
          }
          onClick={onSavePlan}
        >
          {editPlanData ? 'Done' : 'Create Plan'}
        </ButtonV3>
      </div>
    </Modal>
  );
};
